import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { MoneyInput } from "@/components/MoneyInput";

const FIELD = "w-full border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none";
const naira = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 });

// HR proposes a salary / other-allowance change; an Admin approves it later.
export const PayChangeRequestDialog = ({ employee, onClose, onSubmitted }) => {
    const [form, setForm] = useState({
        new_salary: employee.salary || 0,
        new_other_allowance: employee.other_allowance || 0,
        effective_date: new Date().toISOString().slice(0, 10),
        reason: "",
    });
    const [saving, setSaving] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async () => {
        if (!form.reason.trim()) return toast.error("Please give a reason");
        setSaving(true);
        try {
            const num = (v) => (String(v).trim() === "" ? null : Number(v));
            const sal = num(form.new_salary);
            const oa = num(form.new_other_allowance);
            if (sal !== null && sal < 0) { setSaving(false); return toast.error("Salary cannot be negative"); }
            if ((sal ?? Number(employee.salary || 0)) === Number(employee.salary || 0) && (oa ?? Number(employee.other_allowance || 0)) === Number(employee.other_allowance || 0)) {
                setSaving(false); return toast.error("Nothing changed");
            }
            await api.post("/pay-change-requests", {
                employee_id: employee.id,
                new_salary: sal !== null && sal !== Number(employee.salary || 0) ? sal : null,
                new_other_allowance: oa !== null && oa !== Number(employee.other_allowance || 0) ? oa : null,
                effective_date: form.effective_date,
                reason: form.reason,
            });
            toast.success("Request sent to Admin for approval");
            window.dispatchEvent(new Event("badges:refresh"));
            onSubmitted?.();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not send request");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="pay-change-request-dialog">
            <div className="bg-white w-full max-w-md p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between">
                    <div>
                        <div className="overline">Needs Admin approval</div>
                        <h3 className="mt-1 font-display font-bold text-xl">Request pay change</h3>
                    </div>
                    <button onClick={onClose} className="text-ink/50 hover:text-ink" data-testid="pay-change-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="mt-6 grid gap-4">
                    <div className="text-sm text-ink/60">Current: {naira(employee.salary)} salary · {naira(employee.other_allowance)} other allowance</div>
                    <div>
                        <label className="overline block mb-2">New monthly salary (₦)</label>
                        <MoneyInput value={form.new_salary} onChange={set("new_salary")} className={FIELD} data-testid="pay-change-salary-input" />
                    </div>
                    <div>
                        <label className="overline block mb-2">New other allowance (₦)</label>
                        <MoneyInput value={form.new_other_allowance} onChange={set("new_other_allowance")} className={FIELD} data-testid="pay-change-allowance-input" />
                    </div>
                    <div>
                        <label className="overline block mb-2">Effective date</label>
                        <input type="date" value={form.effective_date} onChange={set("effective_date")} className={FIELD} data-testid="pay-change-date-input" />
                    </div>
                    <div>
                        <label className="overline block mb-2">Reason *</label>
                        <textarea rows={3} value={form.reason} onChange={set("reason")} className={FIELD} placeholder="e.g. Promotion to Senior Officer" data-testid="pay-change-reason-input" />
                    </div>
                </div>
                <button onClick={submit} disabled={saving} className="mt-6 w-full bg-klein text-white py-3 text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="pay-change-submit">
                    {saving ? "Sending…" : "Send for approval"}
                </button>
            </div>
        </div>
    );
};

export default PayChangeRequestDialog;
